<template>
  <template v-if="allowed">
    <v-list-item v-if="menu" prepend-icon="mdi-account-switch-outline" :disabled="disabled" @click.stop="open">
      <v-list-item-title>{{ $t('ncrm.contacts.conversion.single') }}</v-list-item-title>
    </v-list-item>
    <v-btn v-else color="primary" variant="outlined" :disabled="disabled" @click.stop="open">
      <v-icon start>mdi-account-switch-outline</v-icon>{{ $t('ncrm.contacts.conversion.bulk') }}
    </v-btn>
    <NcrmRecordEditDialog
      v-if="isOpen" :model-value="isOpen" :title="$t('ncrm.contacts.conversion.bulk')"
      :subtitle="$t('ncrm.contacts.conversion.description')" header-icon="mdi-account-switch-outline"
      :loading="busy" :persistent="busy" :close-disabled="busy" :cancel-disabled="busy"
      :save-disabled="loading || busy || !pending.length || confirmText !== 'DELETE'"
      :save-label="$t('ncrm.contacts.conversion.confirm')"
      @update:model-value="isOpen = $event" @save="convert"
    >
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-alert v-if="error" type="error" class="mb-4">{{ error }}</v-alert>
      <p class="mb-4 text-body-2">{{ $t('ncrm.contacts.conversion.associatedPeople') }}</p>
      <v-list class="pa-0" lines="three">
        <v-list-item v-for="row in rows" :key="row.id" class="px-0">
          <v-list-item-title class="text-wrap conversion-name">{{ row.name }}</v-list-item-title>
          <v-list-item-subtitle class="text-wrap conversion-name">{{ row.proposed }}</v-list-item-subtitle>
          <p v-if="row.code" class="text-error text-body-2" role="alert">{{ failure(row.code) }}</p>
          <router-link v-if="row.person_id" :to="`/ncrm/people/${row.person_id}`" class="text-primary">
            {{ $t('ncrm.contacts.conversion.openPerson') }}
          </router-link>
        </v-list-item>
      </v-list>
      <v-alert v-if="convertedCount" type="success" class="my-4" role="status">
        {{ $t('ncrm.contacts.conversion.result', { count: convertedCount, total: rows.length }) }}
      </v-alert>
      <v-text-field
        v-if="pending.length" v-model="confirmText" :label="$t('ncrm.contacts.conversion.confirmLabel')"
        class="mt-4" :disabled="busy || loading" autocomplete="off" autofocus
      />
    </NcrmRecordEditDialog>
  </template>
</template>

<script>
import axios from "axios";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import { canConvertCompanies, conversionName, conversionFailureKey } from "./companyConversion.mjs";

export default {
  name: "NcrmCompanyConversionAction",
  components: { NcrmRecordEditDialog },
  props: {
    ids: { type: Array, default: () => [] },
    menu: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ["converted"],
  data: () => ({ isOpen: false, loading: false, busy: false, confirmText: "", rows: [], error: "" }),
  computed: {
    allowed: () => canConvertCompanies(),
    pending() { return this.rows.filter((row) => !row.person_id && row.loaded); },
    convertedCount() { return this.rows.filter((row) => row.person_id).length; },
  },
  methods: {
    failure(code) { return this.$t(`ncrm.contacts.conversion.errors.${conversionFailureKey(code)}`); },
    async open() {
      const ids = [...new Set(this.ids.map(Number))];
      this.rows = []; this.error = ""; this.confirmText = ""; this.isOpen = true;
      if (!ids.length || ids.length > 100) {
        this.error = this.$t('ncrm.contacts.conversion.limit'); return;
      }
      this.loading = true;
      const rows = await Promise.all(ids.map(async (id) => {
        try {
          const { data } = await axios.get(`/ncrm/companies/${id}`);
          return { id, name: data.name, proposed: conversionName(data), loaded: true };
        } catch { return { id, name: `#${id}`, code: "conversion_not_found", loaded: false }; }
      }));
      this.rows = rows;
      this.loading = false;
    },
    async convert() {
      if (this.busy || this.loading || this.confirmText !== "DELETE" || !this.pending.length) return;
      this.busy = true; this.error = "";
      try {
        const { data } = await axios.post('/ncrm/companies/convert-to-people', {
          ids: this.pending.map((row) => row.id), confirmText: this.confirmText,
          expectedNames: Object.fromEntries(this.pending.map((row) => [row.id, row.proposed])),
        });
        this.rows = this.rows.map((row) => {
          const result = data.results?.find((item) => item.id === row.id);
          return result ? { ...row, ...result } : row;
        });
        const succeeded = data.results?.filter((row) => row.status === 'converted') || [];
        if (succeeded.length) this.$emit('converted', data.results);
        if (this.menu && succeeded.length === 1) {
          this.isOpen = false;
          await this.$router.replace(`/ncrm/people/${succeeded[0].person_id}`);
        }
      } catch { this.error = this.failure('conversion_failed'); }
      finally { this.busy = false; }
    },
  },
};
</script>

<style scoped>
.conversion-name {
  display: block;
  overflow-wrap: anywhere;
  -webkit-line-clamp: unset;
}
</style>
